import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { INVOICE_KEYS, MONEY, parseDecimal, type InvoiceListDto, type InvoiceOptionsDto, type InvoicesDto, type PartyDto, type PartyInput, type PartyKind, type Pricing, type VoucherListDto, type VouchersDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api, ApiFailure } from "../../lib/api";
import { errorText, fieldErrorsOf, fieldText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, Pending, type Column } from "../../components/ListView";
import { Confirm, Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag, money } from "../settings/common";
import { dec, pendingCell } from "../items/common";
import { AccName, AccountSelect, useAccountOptions } from "../accounting/common";
import { amt, DrCr, when } from "../ledger/common";
import { CustomerHistory } from "./CustomerHistory";

// Sales › Clients and Purchases › Suppliers, ported from the prototype (erp-sales.js partyList / partyForm /
// renderParty) and the audited dialogs. The account index is stored since phase 6b (rule C-1, provisional): a new party
// with none chosen takes the organization's default. The balance is that of the posted lines tagged with the party
// (rule L-5). The open balance waits for opening-balance documents (phase 7) and is shown pending, never computed.

interface Kind { kind: PartyKind; path: string; route: string; group: string; listKey: string; newKey: string; editKey: string; nameKey: string }
const KINDS: Record<PartyKind, Kind> = {
  customer: { kind: "customer", path: "/customers", route: "/Customer", group: "sales", listKey: "clients", newKey: "newClient", editKey: "editCustomer", nameKey: "customerName" },
  supplier: { kind: "supplier", path: "/suppliers", route: "/Supplier", group: "purchases", listKey: "suppliers", newKey: "newSupplier", editKey: "editSupplier", nameKey: "supplierName" }
};
// "" only for a customer migrated without a pricing level (X-43): the user must pick one, never a silent "retail".
type Draft = Omit<PartyInput, "maxWithdrawalLimit" | "pricing" | "accountId"> & { maxWithdrawalLimit: string; pricing: Pricing | ""; accountId: string };
const PRICING: Pricing[] = ["wholesale", "half", "retail"];

function PartyForm({ k, row, onClose, onSaved, quick }: { k: Kind; row: PartyDto | null; onClose(): void; onSaved(p: PartyDto, created: boolean): void; quick?: boolean }) {
  const { t } = useLang();
  const accounts = useAccountOptions();
  const [v, setV] = useState<Draft>(() => row
    ? { name: row.name, phone: row.phone, address: row.address, maxWithdrawalLimit: dec(row.maxWithdrawalLimit), taxNumber: row.taxNumber, commercialRecord: row.commercialRecord, other: row.other, pricing: row.pricing ?? "", accountId: row.accountId ?? "" }
    : { name: "", phone: "", address: "", maxWithdrawalLimit: "0", taxNumber: "", commercialRecord: "", other: "", pricing: "retail", accountId: "" });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (f: keyof Draft, x: string) => { setV((o) => ({ ...o, [f]: x })); setErrs((e) => ({ ...e, [f]: "" })); };
  async function save() {
    const e: Record<string, string> = {};
    if (!v.name.trim()) e.name = t("requiredField");
    if (v.phone.trim() && !/^05\d{8}$/.test(v.phone.trim())) e.phone = t("mobileFormat");
    if (v.taxNumber.trim() && !/^3\d{13}3$/.test(v.taxNumber.trim())) e.taxNumber = t("vatFormat");
    if (k.kind === "customer" && !v.pricing) e.pricing = t("requiredField");
    const limit = parseDecimal(v.maxWithdrawalLimit, MONEY);
    if ("error" in limit) e.maxWithdrawalLimit = limit.error === "out_of_range" ? t("amountTooLarge") : fieldText(t, limit.error);
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    const { pricing, accountId, ...rest } = v;
    // An empty account on a new party takes the organization's default (C-1); on an edit it clears the account.
    const body: PartyInput = { ...rest, maxWithdrawalLimit: v.maxWithdrawalLimit.trim(), ...(k.kind === "customer" && pricing ? { pricing } : {}), accountId: accountId || null };
    setBusy(true); setAlert(null);
    try { onSaved(row ? await api<PartyDto>("PUT", `${k.path}/${row.id}`, body) : await api<PartyDto>("POST", k.path, body), !row); }
    catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); }
    finally { setBusy(false); }
  }
  const txt = (f: "name" | "phone" | "address" | "taxNumber" | "commercialRecord" | "other" | "maxWithdrawalLimit", label: string, o: { ltr?: boolean; ph?: string; full?: boolean; req?: boolean; num?: boolean } = {}) => (
    <Field label={label} req={o.req} full={o.full} error={errs[f]} htmlFor={`pf-${f}`}>
      <input id={`pf-${f}`} className={`control ${o.num ? "num" : ""}`} value={v[f]} onChange={(e) => set(f, e.target.value)} autoComplete="off" placeholder={o.ph}
        {...(o.ltr || o.num ? { dir: "ltr", style: { textAlign: "start" as const } } : {})} {...(o.num ? { inputMode: "decimal" as const } : {})} {...(f === "phone" ? { type: "tel" } : {})} />
    </Field>
  );
  return (
    <Modal title={t(row ? k.editKey : k.newKey)} wide onClose={onClose} onSubmit={save} alert={alert} busy={busy}>
      {txt("name", t("name"), { req: true, full: true })}
      {txt("phone", t("mobileNumber"), { ltr: true, ph: "05XXXXXXXX" })}
      {txt("address", t("address"))}
      {txt("maxWithdrawalLimit", t("maxWithdrawLimit"), { num: true })}
      <Pending label={t("openBalance")} module={t("modOpeningBalances")} />
      {txt("taxNumber", t("taxNumber"), { ltr: true, ph: "3XXXXXXXXXXXXX3" })}
      {txt("commercialRecord", t("commercialRecord"), { ltr: true })}
      {txt("other", t("other"), { full: k.kind === "supplier" })}
      {k.kind === "customer" && (
        <Field label={t("pricing")} htmlFor="pf-pricing" error={errs.pricing}>
          <select id="pf-pricing" className="control" value={v.pricing} onChange={(e) => set("pricing", e.target.value)}>
            {!v.pricing && <option value="">—</option>}
            {PRICING.map((p) => <option key={p} value={p}>{t("price_" + p)}</option>)}
          </select>
        </Field>
      )}
      {/* Added from a sale or purchase by a user who may not see the chart of accounts: the organization's default. */}
      {!(quick && accounts.failed) && <Field label={t("accountIndex")} full={k.kind === "supplier"} error={errs.accountId} htmlFor="pf-account" hint={row ? undefined : t("partyAccountDefaultHint")}>
        <AccountSelect id="pf-account" value={v.accountId} onChange={(x) => set("accountId", x)} opts={accounts.opts} failed={accounts.failed} empty={row ? "–" : t("organizationDefault")} />
      </Field>}
    </Modal>
  );
}

/** A party created from a document, as the document's options list it. */
export const asOption = (p: PartyDto): InvoiceOptionsDto["parties"][number] =>
  ({ id: p.id, kind: p.kind, name: p.name, accountId: p.accountId, balance: p.balance, pending: "0.00", taxNumber: p.taxNumber, phone: p.phone, pricing: p.pricing });

/**
 * "+ Add customer" (or supplier) beside a party selector in a sale, purchase or the POS (correction pass 2026-10-27,
 * item 3). It opens the same form as Clients and Suppliers, in a dialog over the document: the document's lines,
 * fields and payments stay as they are whether the party is created, the dialog is cancelled or the form is refused.
 * The new party is handed back to be selected. Shown only with the keys the server checks (accounts, accountAdd).
 */
export function AddPartyButton({ kind, onCreated, testId }: { kind: PartyKind; onCreated(p: PartyDto): void; testId?: string }) {
  const { t } = useLang();
  const s = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  if (!s.can("accounts") || !s.can("accountAdd")) return null;
  const k = KINDS[kind];
  return <>
    <button type="button" className="btn btn-icon" onClick={() => setOpen(true)} title={t(k.newKey)} aria-label={t(k.newKey)} data-testid={testId ?? "add-party"}><Icon name="userPlus" /></button>
    {open && createPortal(<div className="party-quick" data-testid="add-party-dialog"><PartyForm k={k} row={null} quick onClose={() => setOpen(false)} onSaved={(p) => { setOpen(false); toast(t("addedOk", { x: p.name })); onCreated(p); }} /></div>, document.body)}
  </>;
}

function PartyList({ k }: { k: Kind }) {
  const { t } = useLang();
  const s = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const [rows, setRows] = useState<PartyDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<PartyDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<PartyDto | null>(null);

  const load = useCallback(async () => {
    setFailed(null);
    const p = new URLSearchParams();
    for (const [f, q] of [["name", "q"], ["mobile", "phone"], ["cr", "commercialRecord"], ["vat", "taxNumber"], ["addr", "address"]] as const) if (filters[f]) p.set(q, filters[f]!);
    try { setRows(await api<PartyDto[]>("GET", `${k.path}${p.size ? "?" + p : ""}`)); }
    catch (e) { setFailed(errorText(t, e)); }
  }, [k.path, filters, t]);
  useEffect(() => { void load(); }, [load]);

  async function doDelete(p: PartyDto) {
    setDeleting(null);
    try { await api("DELETE", `${k.path}/${p.id}`); toast(t("deletedOk", { x: p.name })); }
    catch (e) { toast(e instanceof ApiFailure && e.code === "in_use" ? t("cantDeleteParty") : errorText(t, e)); }
    await load();
  }
  const dash = <span className="muted">–</span>;

  const columns: Column<PartyDto>[] = [
    { k: "name", label: t("name"), csv: (p) => p.name, cell: (p) => <a className="cell-name strong" href={`${k.route}/${p.id}`} dir="auto" onClick={(e) => { e.preventDefault(); nav(`${k.route}/${p.id}`); }}>{p.name}</a> },
    { k: "mobile", label: t("mobileNumber"), cell: (p) => (p.phone ? <span className="num" dir="ltr">{p.phone}</span> : dash), csv: (p) => p.phone },
    { k: "cr", label: t("commercialRecord"), cell: (p) => (p.commercialRecord ? <span className="num">{p.commercialRecord}</span> : dash), csv: (p) => p.commercialRecord },
    { k: "open", label: t("openBalance"), end: true, cell: () => pendingCell(t, t("modOpeningBalances")), csv: () => "" },
    { k: "bal", label: t("balance"), end: true, cell: (p) => <DrCr v={p.balance} />, csv: (p) => p.balance },
    { k: "limit", label: t("maxWithdrawLimit"), end: true, cell: (p) => (Number(p.maxWithdrawalLimit) ? <span className="num">{money(Number(p.maxWithdrawalLimit))}</span> : dash), csv: (p) => p.maxWithdrawalLimit }
  ];
  const newBtn = s.can("accountAdd") ? <button className="btn btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />{t(k.newKey)}</button>
    : <LockedButton label={t(k.newKey)} title={t("noPermissionFor", { p: "accountAdd" })} />;

  return (
    <Shell navKey={k.route}>
      <PageHead crumbs={[[t("home"), "/home"], [t(k.group)], [t(k.listKey)]]} title={t(k.listKey)} />
      <ListView rows={rows} error={failed} columns={columns} file={k.listKey} testId={`${k.kind}-table`} onRefresh={() => void load()}
        search={[
          { k: "name", label: t(k.nameKey), type: "text" }, { k: "mobile", label: t("mobileNumber"), type: "text" }, { k: "cr", label: t("commercialRecord"), type: "text" },
          { k: "vat", label: t("taxNumber"), type: "text" }, { k: "addr", label: t("address"), type: "text" }
        ]} filters={filters} onFilters={setFilters}
        quick={(p) => `${p.name} ${p.phone} ${p.commercialRecord} ${p.taxNumber}`} newButton={newBtn}
        empty={{ icon: "users", title: t("noResults"), body: t("noResultsBody") }}
        actions={(p) => [
          { label: t("view"), icon: "eye", run: () => nav(`${k.route}/${p.id}`) },
          { label: t("edit"), icon: "edit", run: () => setEditing(p), ...(s.can("accountUpdate") ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => setDeleting(p), ...(s.can("accountDelete") ? {} : lockTag(t)) }
        ]} />
      {editing && <PartyForm k={k} row={editing === "new" ? null : editing} onClose={() => setEditing(null)}
        onSaved={(p, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: p.name })); void load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => void doDelete(deleting)} />}
    </Shell>
  );
}

/** Posted, unreversed opening balances (rule O-2), debit less credit, with a link to the latest. */
function OpeningTile({ list }: { list: VoucherListDto[] }) {
  const { t } = useLang();
  const live = list.filter((x) => x.status === "posted" && !x.reversal);
  if (!live.length) return <><b className="num muted" data-testid="party-opening">–</b><small>{t(list.length ? "openingDraftsOnly" : "noOpening")}</small></>;
  let c = 0n;
  for (const x of live) { const [i, f = "00"] = x.total.split("."); const v = BigInt(i!) * 100n + BigInt(f.slice(0, 2).padEnd(2, "0")); c += x.side === "credit" ? -v : v; }
  const v = `${c < 0n ? "-" : ""}${(c < 0n ? -c : c) / 100n}.${String((c < 0n ? -c : c) % 100n).padStart(2, "0")}`;
  return <><b data-testid="party-opening"><DrCr v={v} /></b><small>{live.map((x) => <Link key={x.id} to={`/opening-balance/${x.id}`} className="num" style={{ marginInlineEnd: 6 }}>{x.code}</Link>)}</small></>;
}

/** Client or supplier profile (a Delta addition in the prototype: the audit has the list and dialog only). */
function PartyDetail({ k }: { k: Kind }) {
  const { id } = useParams();
  const { t } = useLang();
  const s = useSession();
  const toast = useToast();
  const [p, setP] = useState<PartyDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [vouchers, setVouchers] = useState<VoucherListDto[] | null>(null);
  const vKind = k.kind === "customer" ? "collect" : "payment";
  const load = useCallback(() => api<PartyDto>("GET", `${k.path}/${id}`).then(setP, (e) => setFailed(e instanceof ApiFailure && e.status === 404 ? t("partyNotFound") : errorText(t, e))), [k.path, id, t]);
  useEffect(() => { setP(null); setFailed(null); void load(); }, [load]);
  // The party's collections or payments, when the user may see vouchers (scope applies on the server).
  const canVouchers = s.can("monies");
  useEffect(() => {
    if (!canVouchers || !id) return;
    api<VouchersDto>("GET", `/money-vouchers?kind=${vKind}&partyId=${encodeURIComponent(id)}`).then((x) => setVouchers(x.vouchers), () => setVouchers(null));
  }, [id, vKind, canVouchers]);
  // Opening balances (rule O-1) and the party's invoices (sales or purchases), each when the user may see them.
  const [openings, setOpenings] = useState<VoucherListDto[] | null>(null);
  const [invoices, setInvoices] = useState<InvoiceListDto[] | null>(null);
  const iKind = k.kind === "customer" ? "sale" : "purchase";
  const canInvoices = s.can(INVOICE_KEYS[iKind].view);
  useEffect(() => {
    if (!id) return;
    api<VouchersDto>("GET", `/opening-balances?partyId=${encodeURIComponent(id)}`).then((x) => setOpenings(x.vouchers), () => setOpenings(null));
    if (canInvoices) api<InvoicesDto>("GET", `/invoices?kind=${iKind}&partyId=${encodeURIComponent(id)}`).then((x) => setInvoices(x.invoices), () => setInvoices(null));
  }, [id, iKind, canInvoices]);
  const crumbs: [string, string?][] = [[t("home"), "/home"], [t(k.group)], [t(k.listKey), k.route], ...(p ? [[p.name] as [string]] : [])];
  if (!p) return (
    <Shell navKey={k.route}><PageHead crumbs={crumbs} title={t(k.listKey)} />
      <section className="card"><div className={`state-box ${failed ? "error" : ""}`}>{failed ? <><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p></> : <p>{t("loading")}</p>}</div></section>
    </Shell>
  );
  const initials = p.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("");
  const waiting = (m: string) => <><b className="num muted">–</b><small>{t("waitsFor", { m })}</small></>;
  return (
    <Shell navKey={k.route}>
      <PageHead crumbs={crumbs} title={p.name} badge={<span className="tag-addition">{t("newInDelta")}</span>} />
      <section className="card profile">
        <div className="pf-head"><span className="pf-avatar">{initials}</span>
          <div className="pf-id"><h2 dir="auto">{p.name}</h2><div className="pf-meta">
            {p.phone && <span><Icon name="phone" /><span className="num" dir="ltr">{p.phone}</span></span>}
            {p.taxNumber && <span>{t("vatNo")}: <b className="num">{p.taxNumber}</b></span>}
            {p.commercialRecord && <span>{t("commercialRecord")}: <b className="num">{p.commercialRecord}</b></span>}
            {p.pricing && <span>{t("pricing")}: <b>{t("price_" + p.pricing)}</b></span>}
            {p.address && <span>{p.address}</span>}
          </div></div>
          <div className="pf-actions">
            {s.can("moneyAdd") && <Link className="btn" data-testid="party-new-voucher" to={`${k.kind === "customer" ? "/receipts/Collect" : "/receipts/Payment"}?new=1&party=${p.id}`}><Icon name="receipt" />{t(k.kind === "customer" ? "newCollect" : "newPayment")}</Link>}
            {s.can("accountUpdate") && <button className="btn" onClick={() => setEditing(true)}><Icon name="edit" />{t("edit")}</button>}
          </div>
        </div>
        <div className="pf-tiles">
          <div className="tile"><span>{t("balance")}</span><b data-testid="party-balance"><DrCr v={p.balance} /></b><small>{t("partyBalanceHint")}</small></div>
          <div className="tile"><span>{t("accountIndex")}</span><b style={{ fontSize: 14, fontWeight: 500 }} data-testid="party-account"><AccName a={p.account} /></b>{!p.account && <small>{t("partyNoAccount")}</small>}</div>
          <div className="tile"><span>{t("openBalance")}</span>{openings ? <OpeningTile list={openings} /> : waiting(t("openingBalances"))}
            {s.can("accountUpdate") && <Link className="btn btn-sm btn-ghost" style={{ marginTop: 6 }} data-testid="party-new-opening" to={`/opening-balances?new=1&party=${p.id}`}><Icon name="plus" />{t("newOpening")}</Link>}</div>
          <div className="tile"><span>{t("maxWithdrawLimit")}</span><b className="num" data-testid="party-limit">{Number(p.maxWithdrawalLimit) ? money(Number(p.maxWithdrawalLimit)) : "–"}</b><small>{Number(p.maxWithdrawalLimit) ? t("currency") : t("noLimit")}</small></div>
          <div className="tile"><span>{t("other")}</span><b style={{ fontSize: 14, fontWeight: 500 }}>{p.other || "–"}</b></div>
        </div>
      </section>
      {vouchers && <section className="card" style={{ marginTop: 18 }}>
        <div className="card-head"><h2>{t(k.kind === "customer" ? "collects" : "payments")} <span className="count num">{vouchers.length}</span></h2>
          <Link className="btn btn-sm" to={k.kind === "customer" ? "/receipts/Collect" : "/receipts/Payment"}><Icon name="receipt" />{t("openList")}</Link></div>
        <div className="table-wrap"><table className="tbl" data-testid="party-vouchers">
          <thead><tr><th>{t("receiptId")}</th><th>{t("date")}</th><th className="end">{t("amount")}</th><th>{t("note")}</th></tr></thead>
          <tbody>{vouchers.length ? vouchers.map((x) => <tr key={x.id}><td><Link className="num" to={`/receipt/${x.id}`}>{x.code ?? t("journalDraft")}</Link></td><td className="num">{when(x.voucherAt)}</td><td className="end num">{amt(x.total)}</td><td>{x.note || "–"}</td></tr>)
            : <tr><td colSpan={4} className="muted">{t("noVouchers")}</td></tr>}</tbody>
        </table></div>
      </section>}
      {k.kind === "customer" && s.can("invoiceSales") && <section className="card" style={{ marginTop: 18 }}>
        <div className="card-head"><h2>{t("chHistory")}</h2></div>
        <div className="card-body"><CustomerHistory partyId={p.id} /></div>
      </section>}
      {invoices && k.kind !== "customer" && <section className="card" style={{ marginTop: 18 }}>
        <div className="card-head"><h2>{t("purchasesInvoices")} <span className="count num">{invoices.length}</span></h2>
          <Link className="btn btn-sm" to="/invoices/Purchase"><Icon name="receipt" />{t("openList")}</Link></div>
        <div className="table-wrap"><table className="tbl" data-testid="party-invoices">
          <thead><tr><th>{t("invoiceNo")}</th><th>{t("date")}</th><th className="end">{t("total")}</th><th className="end">{t("paid")}</th><th className="end">{t("settledAmount")}</th><th className="end">{t("remaining")}</th></tr></thead>
          <tbody>{invoices.length ? invoices.map((x) => <tr key={x.id}><td><Link className="num" to={`/invoices/show/${x.id}`}>{x.code ?? t("journalDraft")}</Link></td><td className="num">{when(x.invoiceAt)}</td>
            <td className="end num">{amt(x.grand)}</td><td className="end num">{amt(x.paid)}</td><td className="end num">{amt(x.settled)}</td><td className="end num">{amt(x.remaining)}</td></tr>)
            : <tr><td colSpan={6} className="muted">{t("noInvoicesFor")}</td></tr>}</tbody>
        </table></div>
      </section>}
      {editing && <PartyForm k={k} row={p} onClose={() => setEditing(false)} onSaved={(x) => { setEditing(false); setP(x); toast(t("updatedOk", { x: x.name })); }} />}
    </Shell>
  );
}

export const Customers = () => <PartyList k={KINDS.customer} />;
export const Suppliers = () => <PartyList k={KINDS.supplier} />;
export const CustomerDetail = () => <PartyDetail k={KINDS.customer} />;
export const SupplierDetail = () => <PartyDetail k={KINDS.supplier} />;
